import { isRequestError, request } from "@prismicio/editor-support/Http"
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardHeaderTitle,
  Dialog,
  Form,
  FormInput,
  Icon,
  Text,
  useMediaQuery,
} from "@prismicio/editor-ui"
import { get as getCookie } from "es-cookie"
import { useState } from "react"
import { z } from "zod"

import { env } from "../../../app/env"
import styles from "./MobileBlocker.module.css"
import mobileVideoPoster from "./video-poster.jpg"

const HUBSPOT_PAGE_URI = "/try/"
const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again."

export function MobileBlocker() {
  const open = useMediaQuery({ max: "medium" })

  return (
    <Dialog open={open} size="fullscreen" modal>
      <div className={styles["content"]}>
        <Card paddingInline={16} paddingBlock={16}>
          <CardHeader>
            <CardHeaderTitle variant="h3">
              This demo isn&rsquo;t ready for mobile yet
            </CardHeaderTitle>
          </CardHeader>
          <CardContent>
            <div className={styles["cardContent"]}>
              <Text>
                Until then, you can watch this short video to see the
                experience.
              </Text>
              <video poster={mobileVideoPoster} controls preload="metadata">
                <source
                  src="https://res.cloudinary.com/frontendhorse/video/upload/f_auto,q_auto/v1697464154/Page_Builder_Demo_V3_ma913z.mov"
                  type="video/mp4"
                />
                <track kind="captions" />
              </video>
              <Text>
                To experience Prismic yourself, open this demo on a desktop.
                Enter your email below to get a link sent to you.
              </Text>
              <EmailForm />
            </div>
          </CardContent>
        </Card>
      </div>
    </Dialog>
  )
}

const emailLinkOkSchema = z.union([
  z.object({ success: z.literal(true) }),
  z.object({ error: z.string() }),
])

function EmailForm() {
  const [isComplete, setIsComplete] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [email, setEmail] = useState("")
  const [emailError, setEmailError] = useState<string>()
  const [company, setCompany] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  function onEmailChange(value: string) {
    setEmail(value)
    if (emailError) setEmailError(undefined)
  }

  async function onSubmit() {
    if (!email) {
      setEmailError("This field is required")
      return
    }

    setError(undefined)
    setIsSubmitting(true)

    const hutk = getCookie("hubspotutk")
    const body = { email, company, hutk, pageUri: HUBSPOT_PAGE_URI }

    try {
      await request(new URL("email-link", env.PRISMIC_DEMO_API_BASE_URL), {
        method: "POST",
        body,
        okType: emailLinkOkSchema,
      })
      setIsSubmitting(false)
      setIsComplete(true)
    } catch (error) {
      setIsSubmitting(false)
      if (!isRequestError(error) || error.detail.type !== "http") {
        setError(DEFAULT_ERROR_MESSAGE)
        return
      }

      const parsed = z
        .object({ error: z.string() })
        .safeParse(error.detail.body)

      if (parsed.success) {
        setError(parsed.data.error)
      } else {
        setError(DEFAULT_ERROR_MESSAGE)
      }
    }
  }

  return (
    <Card paddingBlock={16} paddingInline={16} color="grey2" variant="outlined">
      <CardContent>
        {isComplete ? (
          <Text>
            Check your email for the link to open the demo on your desktop!
          </Text>
        ) : (
          <Form onSubmit={onSubmit}>
            <FormInput
              label="Email"
              type="email"
              placeholder="Enter your email"
              value={email}
              error={emailError}
              onValueChange={onEmailChange}
              disabled={isSubmitting}
            />
            <FormInput
              label="Company"
              type="text"
              placeholder="Enter your company name"
              value={company}
              onValueChange={setCompany}
              disabled={isSubmitting}
            />
            <Button size="large" loading={isSubmitting}>
              Submit
            </Button>
            {error ? (
              <Text color="tomato11">
                <Icon
                  name="alert"
                  size="small"
                  color="tomato11"
                  className={styles["errorIcon"]}
                />{" "}
                {error}
              </Text>
            ) : null}
          </Form>
        )}
      </CardContent>
    </Card>
  )
}
